import { useContext, useState } from "react";
import {
  Autocomplete,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import dayjs from "dayjs";
import { titleize } from "inflection";
import { map } from "lodash";
import { map as mapFp } from "lodash/fp";
import ReminderSection from "./ReminderSection";
import { useMutation, useQuery } from "@apollo/client/react";
import ImageSection from "@/components/ImageSection";
import OverwatchModal from "@/components/OverwatchModal";
import CurrentUserContext from "@/Contexts/CurrentUser";
import { NotesTypeEnum, GetPersonalNotesQuery, Project, NotesCreate, CreateNotesDocument, UpdateNotesDocument, GetAllReportsDocument, PermissionActionEnum, PermissionEntityEnum, GetProjectsDocument, GetCompanyTagsDocument, AttachedFileFragment } from "@/graphql/generated/graphql";
import useFileUploader from "@/hooks/useFileUploader";
import { MAX_ATTACHMENTS_ALLOWED } from "@/utils/constants";
import { arrayLength } from "@/utils/helperFunctions";
import { displayNotification } from "@/utils/notifications";
import { MinimizedModalsType } from "@/types/globals";
import PermissionContext from "@/Contexts/Permissions";

const DEFAULT_NOTE_DATA = {
  description: "",
  notesType: NotesTypeEnum.Personal,
  tagIds: [],
  targetId: "",
  targetType: "",
  reminder: "",
}

interface CreateNoteModalProps {
  minimized?: boolean;
  handleClose: () => void;
  handleMinimize?: (label: MinimizedModalsType["label"]) => void;
  currentNote?: NonNullable<GetPersonalNotesQuery['getPersonalNotes']>[0] | null;
  notesType?: NotesTypeEnum;
  targetId?: string;
  targetType?: string;
}

type OptionType = {
  id: string;
  code: string;
  project: Project;
  reportDate: string
}

const mapNode = mapFp("node");
const mapSignedBlobId = mapFp("signedBlobId");

const CreateNoteModal: React.FC<CreateNoteModalProps> = ({
  minimized = false,
  handleClose,
  handleMinimize = () => { },
  currentNote = null,
  notesType,
  targetId,
  targetType
}) => {
  const { currentUser } = useContext(CurrentUserContext);
  const { canPerformAction } = useContext(PermissionContext);
  const [noteData, setNoteData] = useState<NotesCreate>(
    {
      ...DEFAULT_NOTE_DATA,
      description: currentNote?.description || "",
      notesType: currentNote?.notesType || notesType || NotesTypeEnum.Personal,
      tagIds: map(currentNote?.tags || [], (tag) => tag.id),
      targetId: currentNote?.targetId || targetId || "",
      targetType: currentNote?.targetType || targetType || "",
      reminder: currentNote?.reminder || ""
    }
  );

  const [createNote, { loading }] = useMutation(CreateNotesDocument, {
    refetchQueries: ["getPersonalNotes"],
    onCompleted: () => {
      displayNotification({ type: "success", text: "Note created successfully!" });
      handleClose();
    },
    onError: (error) => displayNotification({ type: "error", text: error.message }),
  });

  const [updateNote] = useMutation(UpdateNotesDocument, {
    refetchQueries: ["getPersonalNotes"],
    onCompleted: () => {
      displayNotification({ type: "success", text: "Note updated successfully!" });
      handleClose();
    },
    onError: (error) => displayNotification({ type: "error", text: error.message }),
  });

  const { isUploading, files, handleUploadImage, handleRemoveImage } = useFileUploader();
  const { data: reports } = useQuery(GetAllReportsDocument, {
    skip: !(currentUser?.isOwner || canPerformAction(PermissionActionEnum.View, PermissionEntityEnum.Reports))
  });
  const { data: projects } = useQuery(GetProjectsDocument, {
    fetchPolicy: "cache-and-network",
    skip: !currentUser?.defaultCompany?.id ||
      !(currentUser?.isOwner || canPerformAction(PermissionActionEnum.View, PermissionEntityEnum.Projects)),
    variables: {
      companyId: currentUser?.defaultCompany?.id as string,
    },
  });

  const { data: tagsData } = useQuery(GetCompanyTagsDocument);

  const handleSubmit = () => {
    const { description, notesType, tagIds, targetId, targetType, reminder } = noteData;
    if (currentUser) {
      const data = {
        description,
        notesType,
        tagIds,
        targetId,
        targetType,
        reminder,
        signedIds: mapSignedBlobId(files)
      }

      if (currentNote) {
        updateNote({
          variables: {
            noteData: {
              ...data,
              id: currentNote.id,
            }
          }
        });
      }
      else {
        createNote({
          variables: {
            noteData: data,
          }
        });
      }
    }
  };

  const renderEntitySelect = () => {
    if (noteData.notesType === NotesTypeEnum.Personal) {
      return null;
    }

    const options = noteData.notesType === NotesTypeEnum.Report
      ? mapNode(reports?.getCompanyReports?.edges) || []
      : mapNode(projects?.getProjects?.edges) || [];

    const getOptionLabel = (option: OptionType) => {
      if (noteData.notesType === NotesTypeEnum.Report) {
        return `Report ${option.project.code} ${option.reportDate}`;
      }
      return option.code || '';
    };

    return (
      <Grid size={12}>
        <Autocomplete
          value={options.find(opt => opt.id === noteData.targetId) || null}
          options={options}
          getOptionLabel={getOptionLabel}
          onChange={(_, newValue) => {
            setNoteData(prev => ({
              ...prev,
              targetId: newValue?.id || '',
              targetType: titleize(noteData.notesType)
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Select ${titleize(noteData.notesType)}`}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <OverwatchModal
      title="New Note"
      caption="Create private or company shared notes"
      size="sm"
      handleClose={handleClose}
      handleMinimize={() => handleMinimize(noteData?.description || 'New Note')}
      primaryButtonText={
        isUploading
          ? "Uploading..."
          : loading
            ? currentNote ? "Updating..." : "Creating..."
            : currentNote ? "Update" : "Create"
      }
      primaryButtonAction={handleSubmit}
      disablePrimary={noteData.description.trim().length === 0 || isUploading || loading}
      secondaryButtonAction={handleClose}
      secondaryButtonText="Cancel"
      minimized={minimized}
    >
      <Grid container>

        <ToggleButtonGroup
          exclusive
          color="primary"
          value={noteData.notesType}
          onChange={(_, selectedNotesType: NotesTypeEnum) => {
            if (!selectedNotesType) return;
            setNoteData((prev) => ({
              ...prev,
              notesType: selectedNotesType,
              targetId: "",
              targetType: "",
            }));
          }}
          size="small"
          sx={{ mb: 2 }}
        >
          {Object.keys(NotesTypeEnum).map((type) =>
            <ToggleButton
              size="small"
              key={NotesTypeEnum[type as keyof typeof NotesTypeEnum]}
              value={NotesTypeEnum[type as keyof typeof NotesTypeEnum]}
            >
              {titleize(NotesTypeEnum[type as keyof typeof NotesTypeEnum])}
            </ToggleButton>
          )}
        </ToggleButtonGroup>

        {renderEntitySelect()}

        <TextField
          fullWidth
          label="Description"
          defaultValue={noteData.description}
          multiline
          rows={4}
          onChange={(e) =>
            setNoteData((prev) => ({
              ...prev,
              description: e.target.value,
            }))
          }
          sx={{ mb: 2 }}
        />

        <Autocomplete
          multiple
          options={map(tagsData?.getCompanyTags, (tag) => ({ id: tag.id, title: tag.title }))}
          getOptionLabel={(option) => option ? option.title : ""}
          value={map((noteData.tagIds ?? []), id => tagsData?.getCompanyTags.find(tag => tag.id === id))}
          onChange={(_, newValue) => {
            setNoteData((prev) => ({
              ...prev,
              tagIds: newValue.map(tag => tag?.id as string),
            }));
          }}
          fullWidth
          renderInput={(params) => (
            <TextField {...params} label="Tags" placeholder="Select tags" fullWidth sx={{ mb: 2 }} />
          )}
        />

        <ReminderSection
          setReminder={(reminderValue) => setNoteData((prev) => ({
            ...prev,
            reminder: reminderValue,
          }))}
          reminderValue={noteData.reminder ? dayjs(noteData.reminder) : null}
          resetReminder={() => setNoteData((prev) => ({
            ...prev,
            reminder: "",
          }))}
        />

        <ImageSection
          attachments={currentNote?.attachments as AttachedFileFragment[] || files}
          handleRemoveImage={handleRemoveImage}
          disableAttaching={arrayLength(files) >= MAX_ATTACHMENTS_ALLOWED || arrayLength(currentNote?.attachments) > 0 || !noteData.description.length || isUploading}
          handleUploadImage={(e: React.ChangeEvent<HTMLInputElement>) => handleUploadImage(e, "notes")}
          isUploading={isUploading}
          hasImages={(arrayLength(files) > 0 || arrayLength(currentNote?.attachments) > 0)}
        />
      </Grid>
    </OverwatchModal>
  );
};

export default CreateNoteModal;
